import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/material.module';
import { DxListModule, DxDropDownBoxModule, DxDropDownBoxComponent, DxTextBoxModule, DxListComponent } from 'devextreme-angular';
import { VistaServices } from 'src/app/data/vistasServices';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClickOutsideDirective } from 'src/app/directives/click-outside.directive';

@Component({
  selector: 'app-search-list',
  standalone: true,
  imports: [CommonModule, MaterialModule, DxListModule, DxDropDownBoxModule, DxTextBoxModule, ClickOutsideDirective],
  templateUrl: './search-list.component.html',
  styleUrls: ['./search-list.component.scss'],
  encapsulation: ViewEncapsulation.Emulated 
})
export class SearchListComponent implements OnInit {

  @Input() vista: string;
  @Input() etiqueta: string;
  @Input() name_id: string;
  @Input() value_id: string;
  @Input() nameFields: string[];
  /** Modo barra de menú: siempre abre y enfoca el cuadro de búsqueda al pulsar la lupa */
  @Input() menuBar = false;

  @Output() handleEditChanges = new EventEmitter<{ event: any }>();
  @ViewChild(DxListComponent, { static: false }) list!: DxListComponent;
  @ViewChild('searchList', { static: false }) searchList!: DxListComponent;

  dataList: { id: any; name: string;}[] = []; 
  selectedValue: any;
  selectedName: string = '';
  showDropDown: boolean;
  selectedItem: string;
  isSearchVisible: boolean = false;

  /** Referencia estable para quitar el listener de Enter al cerrar */
  private readonly searchKeyDownHandler = (event: KeyboardEvent) => this.handleSearchKeyDown(event);

  constructor(
    private vistaService: VistaServices,
    private hostRef: ElementRef<HTMLElement>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: { view: string, value: string, dataNames: string[] }
  ) {
    if (data) {
      // Si el componente es llamado como un diálogo
      this.vista = data.view;
      this.value_id = data.value;
      this.nameFields = data.dataNames;
    }
  }
  

  ngOnInit(): void {
    this.configureColumns();
  }

  configureColumns(): void {
    if (this.name_id && this.value_id) {
      this.showDropDown = false
      this.vistaService.getVista(this.vista, [this.name_id], [this.value_id]).subscribe(
        data => {
          this.selectedItem = data.datos
            .map((item: any) => {
              const rfc = item['rfc'] || '';
              const nombre = item['nombre'] || '';
              const paterno = item['apellido_paterno'] || '';
              const materno = item['apellido_materno'] || '';
              const clave = item['clave_presupuestal_matutino'] || '';
              const patente = item['patente'] || '';
              
              return `${rfc} | ${nombre} ${paterno} ${materno} | ${clave} | ${patente}`;
            })
            .join(', ');
        },
        error => {
          console.error('Error al obtener datos:', error);
        }
      );
    } 
    else {
      this.showDropDown = true
      this.vistaService.getVista(this.vista).subscribe(
        data => {
          this.dataList = data.datos.map((item: any) => {
            const rfc = item['rfc'] || '';
            const nombre = item['nombre'] || '';
            const paterno = item['apellido_paterno'] || '';
            const materno = item['apellido_materno'] || '';
            const clave = item['clave_presupuestal_matutino'] || '';
            const patente = item['patente'] || '';
            
            return {
              id: item.id,
              name: `${rfc} | ${nombre} ${paterno} ${materno} | ${clave} | ${patente}`
            };
          });
        },
        error => {
          console.error('Error al obtener datos:', error);
        }
      );
    }
  }

  /** Abre el popup; en barra de menú siempre despliega y enfoca el campo de búsqueda */
  openSearch(event?: Event): void {
    event?.stopPropagation();
    if (this.menuBar) {
      this.isSearchVisible = true;
    } else {
      this.isSearchVisible = !this.isSearchVisible;
    }
    if (this.isSearchVisible) {
      this.enfocarCampoBusqueda();
    }
  }

  /** Coloca el foco en el textbox de búsqueda del popup (reintenta hasta que DevExtreme lo renderice) */
  private enfocarCampoBusqueda(intentos = 0): void {
    const maxIntentos = 20;
    requestAnimationFrame(() => {
      const searchInput = this.obtenerInputBusqueda();
      if (searchInput) {
        const boton = this.hostRef.nativeElement.querySelector('.search-button') as HTMLElement | null;
        boton?.blur();
        searchInput.focus();
        if (searchInput.value) {
          searchInput.select();
        }
        searchInput.removeEventListener('keydown', this.searchKeyDownHandler);
        searchInput.addEventListener('keydown', this.searchKeyDownHandler);
        return;
      }
      if (intentos < maxIntentos) {
        setTimeout(() => this.enfocarCampoBusqueda(intentos + 1), 50);
      }
    });
  }

  /** Obtiene el input de búsqueda solo dentro del popup de esta instancia */
  private obtenerInputBusqueda(): HTMLInputElement | null {
    const popup = this.hostRef.nativeElement.querySelector('.search-popup');
    if (!popup) {
      return null;
    }
    return (
      popup.querySelector('.dx-list-search input')
      ?? popup.querySelector('.dx-list-search .dx-texteditor-input')
      ?? popup.querySelector('input.dx-texteditor-input')
      ?? popup.querySelector('.dx-texteditor-input')
    ) as HTMLInputElement | null;
  }

  /** Enter en búsqueda: selecciona el primer maestro visible en la lista filtrada */
  handleSearchKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const popup = this.hostRef.nativeElement.querySelector('.search-popup');
      const listItems = popup?.querySelectorAll('.dx-list-item') ?? [];
      if (listItems.length > 0) {
        const firstItem = listItems[0] as HTMLElement;
        firstItem.click();
      }
    }
  }

  onItemSelect(event: any): void {
    if (event && event.itemData) {
      this.selectedValue = event.itemData.id;
      this.selectedName = event.itemData.name;
      this.isSearchVisible = false;
      const searchInput = this.obtenerInputBusqueda();
      if (searchInput) {
        searchInput.removeEventListener('keydown', this.searchKeyDownHandler);
      }
    } else {
      this.selectedValue = null;
      this.selectedName = '';
    }
    this.handleEditChanges.emit({ event });
  }

  onDropDownOpen(): void {
    this.isSearchVisible = true;
    this.handleEditChanges.emit({ event: null });
  }
  
  onDropDownClose(): void {
    this.isSearchVisible = false;
    if (!this.selectedValue) {
      this.handleEditChanges.emit({ event: null }); 
    }
  }  

  reset(): void {
    this.selectedValue = null; 
    this.selectedName = ''; 
  }

  closeSearch(): void {
    this.isSearchVisible = false;
    const searchInput = this.obtenerInputBusqueda();
    if (searchInput) {
      searchInput.removeEventListener('keydown', this.searchKeyDownHandler);
    }
  }
}
